import { AlertDetailDto, AlertHistoryResponse } from './alert';
import { ShipmentRouteResponse } from './shipment-route';

/** 判定された理由の種別 */
export enum ShipmentJudgedReasonType {
  /** デバイスが出発地点にいない */
  DEVICE_NOT_AT_STARTING_POINT = 'device_not_at_starting_point',
  /** デバイスステータスが想定した値でない */
  UNEXPECTED_DEVICE_STATUS = 'unexpected_device_status',
}

/** 遷移元ページを識別するクエリパラメータ名 */
export const SOURCE_PAGE_QUERY_PARAM = 'sourcePage';

/** 遷移元ページ */
export enum SourcePage {
  DASHBOARD = 'dashboard',
  DEVICE_INFO = 'device-info',
}

/** 配送設定デバイスのレスポンス内容 */
export type ShipmentDeviceDto = {
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
  /** バッテリー */
  batteryPercentage?: number;
};

/** 配送設定一覧取得APIのロケーション情報 */
export type ShipmentSettingLocationResponse = {
  /** 配送位置情報ID */
  locationId: string;
  /** 配送位置情報名 */
  locationName: string;
};

/** 配送設定一覧取得APIのデバイス情報 */
export type ShipmentSettingDeviceResponse = {
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
};

/** 配送設定一覧取得APIのレスポンス内容 */
export type ShipmentSettingResponse = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** 繰り返し設定 */
  frequency?: '' | 'once' | 'repetition' | null;
  /** 出発地 */
  departure?: ShipmentSettingLocationResponse;
  /** 到着地 */
  destination?: ShipmentSettingLocationResponse;
  /** デバイス一覧 */
  deviceList: ShipmentSettingDeviceResponse[];
  /** 配送ステータス */
  status?: string;
  /** 更新日時 */
  updatedAt?: string;
};

/** 配送設定APIリクエストのルート情報 */
export type ShipmentRequestRoute = {
  /** ルートID（任意） */
  routeId?: string;
  /** ルート名（任意） */
  routeName?: string | null;
  /** 位置情報リスト */
  locationList: ShipmentRouteResponse['locationList'];
  /** 永続ルートか（任意） */
  permanent?: boolean;
  /** 使用中か（任意） */
  isUse?: boolean;
};

/** 配送設定のレスポンス内容 */
export type ShipmentResponse = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** 配送ステータス */
  status: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 出発実績日時 */
  actualStartDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 到着実績日時 */
  actualArrivalDatetime?: string;
  /** センサーアラート取得開始時間(分数) */
  alertDetectionOffset?: number;
  /** ルート設定 */
  route: ShipmentRouteResponse;
  /** デバイス一覧 */
  deviceList: ShipmentDeviceDto[];
  /** センサーアラート取得開始実績時間 */
  sensorAlertStartTime?: string;
  /** 地図表示用の履歴取得開始時刻 */
  mapHistoryFrom?: string;
};

/** 配送設定の追加リクエスト内容 */
export type ShipmentAddRequest = {
  /** 配送設定名 */
  shipmentName: string;
  /** 繰り返し作成 */
  frequency?: '' | 'once' | 'repetition';
  /** 繰り返し間隔 */
  intervalDays?: number;
  /** 繰り返し曜日 */
  weekdays?: number[];
  /** 繰り返し複数日 */
  specificDates?: string[];
  /** 繰り返し終了日 */
  frequencyEndDate?: string;
  /** 出発予定日時 */
  startDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** センサーアラート取得開始時間(分数) */
  alertDetectionOffset?: number;
  /** ルート設定 */
  route: ShipmentRequestRoute;
  /** デバイス一覧 */
  deviceList: ShipmentDeviceDto[];
};

/** 配送設定の変更リクエスト内容 */
export type ShipmentUpdateRequest = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** 繰り返し作成 */
  frequency?: '' | 'once' | 'repetition';
  /** 出発予定日時 */
  startDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** センサーアラート取得開始時間(分数) */
  alertDetectionOffset?: number;
  /** ルート設定 */
  route: ShipmentRequestRoute;
  /** デバイス一覧 */
  deviceList: ShipmentDeviceDto[];
};

/** 配送毎のステータス一覧のレスポンス内容 */
export type ShipmentStatusDto = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送位置情報ID */
  locationId: string | null;
  /** 配送ステータス */
  status: string;
  /** 発生時間 */
  eventAt: string;
  /** 解除時間 */
  resolvedAt: string | null;
};

/** 配送毎のステータス履歴のレスポンス内容 */
export type ShipmentStatusHistoryResponse = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** 配送ステータス */
  statusList: ShipmentStatusDto[];
};

/** 配送の計算されたルート要素のレスポンス内容 */
export type CalculatedRoutePointResponse = {
  /** 配送位置情報ID */
  locationId: string;
  /** 到着予想時間 */
  arrivalTime: string;
  /** 予想時間間隔 */
  duration: number;
};

/** 配送毎のアラートのレスポンス内容 */
export type ShipmentAlertResponse = {
  /** 配送ID */
  shipmentId: string;
  /** 配送アラート */
  status: string;
  /** 発生時刻 */
  eventAt: string;
  /** 解除時間 */
  resolvedAt?: string;
  /** 判定理由 */
  judgedReason?: ShipmentJudgedReasonType;
};

/** 配送毎のアラート履歴のレスポンス内容 */
export type ShipmentAlertHistoryResponse = {
  /** 配送ID */
  shipmentId: string;
  /** 配送名 */
  shipmentName: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** センサーアラート履歴 */
  alertHistory: ShipmentAlertResponse[];
};

/** 配送毎のセンサーアラート履歴のレスポンス内容 */
export type ShipmentSensorAlertHistoryResponse = {
  /** 配送ID */
  shipmentId: string;
  /** 配送名 */
  shipmentName: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 出発実績日時 */
  actualStartDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 到着実績日時 */
  actualArrivalDatetime?: string;
  /** センサーアラート履歴 */
  alertHistory: AlertHistoryResponse;
  /** 最新のセンサーアラート詳細 */
  latestAlertDetail?: AlertDetailDto;
};

/** 使用可能なデバイスのレスポンス内容 */
export type AvailableDeviceResponse = {
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName?: string;
  /** デバイス設定グループID */
  deviceSettingGroupId?: string;
  /** デバイス設定グループ名 */
  deviceSettingGroupName?: string;
};

/** 配送ステータス判定結果のレスポンス内容 */
export type ShipmentStatusJudgeResponse = {
  /** 判定理由（配送ID:理由） */
  judgedReasons?: Record<string, ShipmentJudgedReasonType>;
};
